import { promises as fs } from "node:fs";
import * as path from "node:path";
import type { Logger } from "pino";

import type { FileAttachment, SessionUpload } from "../../shared/messages.js";

/**
 * Per-workspace storage for chat-attached files (PDFs, archives, .sql/.db,
 * arbitrary binary blobs). The store writes uploads to
 *   `<cwd>/.appostle/tmp/uploaded_files/<id>__<sanitizedName>`
 * and maintains a manifest at `<cwd>/.appostle/tmp/uploaded_files/uploads.json`
 * keyed by upload id.
 *
 * Why inside the workspace, not under $APPOSTLE_HOME?
 *   The whole point is to give the agent (Claude/Codex/OpenCode) a path it
 *   can read with its normal file-read tool. They all run with the workspace
 *   as cwd; anything outside that root is awkward at best and blocked at
 *   worst. Putting the file in `.appostle/tmp/uploaded_files/` inside the
 *   workspace makes the relative path stable and natural.
 *
 * `.appostle/` is appended to the workspace `.gitignore` on first write so
 * uploads don't leak into commits.
 *
 * Backward compatibility: the manifest is JSON with a versioned envelope.
 * Older entries that don't carry future fields still parse — we only ever
 * append optional fields.
 */

/** Single-message size cap. Beyond this we reject the entire send. */
export const MAX_UPLOAD_BYTES_PER_MESSAGE = 50 * 1024 * 1024;

/** Per-file size cap. */
export const MAX_UPLOAD_BYTES_PER_FILE = 50 * 1024 * 1024;

export const UPLOADS_DIR_REL = path.posix.join(".appostle", "tmp", "uploaded_files");
const MANIFEST_BASENAME = "uploads.json";

/**
 * Recover the user-visible filename from a stored upload's relative path.
 * On disk we write `<id>__<sanitizedName>`; the first `__` is always the
 * separator (UUIDs don't contain `__`). Returns `null` when the path is
 * not under the uploads dir or the basename has no `__` separator (raw
 * file, manual drop-in).
 */
export function originalUploadFileName(relativePath: string): string | null {
  const safe = path.posix.normalize(relativePath);
  if (!safe.startsWith(`${UPLOADS_DIR_REL}/`)) return null;
  const baseName = path.posix.basename(safe);
  const sep = baseName.indexOf("__");
  if (sep < 0) return null;
  return baseName.slice(sep + 2);
}
const GITIGNORE_MARKER_LINE = "# Added by Appostle: uploaded chat attachments";
// Ignore everything inside `.appostle/` by default but track `.appostle/brand/`
// so the per-project brand schema is version-controlled. New types of state we
// add under `.appostle/` will be ignored by default — opt them in here if they
// should be tracked.
const GITIGNORE_BLOCK = `${GITIGNORE_MARKER_LINE}\n.appostle/*\n!.appostle/brand/\n`;

interface ManifestEnvelopeV1 {
  version: 1;
  uploads: SessionUpload[];
}

function emptyManifest(): ManifestEnvelopeV1 {
  return { version: 1, uploads: [] };
}

/** Strip path separators, control characters, and edge dots from a user-supplied filename. */
export function sanitizeFileName(input: string): string {
  // biome-ignore lint/suspicious/noControlCharactersInRegex: deliberately stripping control chars
  const stripped = input.replace(/[\\/\x00-\x1F\x7F]/g, "_").trim();
  // Collapse any sequence of dots so we never emit ".." which could traverse out.
  const noTraversal = stripped.replace(/\.{2,}/g, ".");
  // Drop leading dots/spaces (hidden files, awkward paths) and trailing spaces/dots.
  const trimmed = noTraversal.replace(/^[.\s]+|[\s.]+$/g, "");
  if (trimmed.length === 0) {
    return "upload.bin";
  }
  // Cap to 200 chars to leave room for the ULID prefix and avoid filesystem limits.
  return trimmed.slice(0, 200);
}

function manifestDirFor(cwd: string): string {
  return path.join(cwd, UPLOADS_DIR_REL);
}

function manifestPathFor(cwd: string): string {
  return path.join(manifestDirFor(cwd), MANIFEST_BASENAME);
}

function relativeUploadPath(id: string, sanitizedName: string): string {
  return path.posix.join(UPLOADS_DIR_REL, `${id}__${sanitizedName}`);
}

export class UploadSizeError extends Error {
  constructor(
    public readonly totalBytes: number,
    public readonly cap: number,
  ) {
    super(`Upload payload exceeds ${cap} bytes (got ${totalBytes})`);
    this.name = "UploadSizeError";
  }
}

interface SessionUploadStoreOptions {
  logger?: Logger;
}

export interface PersistFilesParams {
  cwd: string;
  agentId: string;
  files: readonly FileAttachment[];
  messageId: string | null;
}

export interface ListUploadsParams {
  cwd: string;
  agentId: string;
}

export interface DeleteUploadParams {
  cwd: string;
  agentId: string;
  uploadId: string;
}

export class SessionUploadStore {
  private readonly logger: Logger | undefined;

  constructor(opts: SessionUploadStoreOptions = {}) {
    this.logger = opts.logger;
  }

  /**
   * Decode and write each file. Throws `UploadSizeError` if either a single
   * file exceeds the per-file cap or the message total exceeds the per-message
   * cap. On success, returns the manifest entries (in the same order as input).
   */
  async persistFiles(params: PersistFilesParams): Promise<SessionUpload[]> {
    const { cwd, agentId, files, messageId } = params;
    if (files.length === 0) {
      return [];
    }

    // Decode upfront so we can enforce caps before touching the filesystem.
    const decoded: { file: FileAttachment; bytes: Buffer }[] = [];
    let totalBytes = 0;
    for (const file of files) {
      let bytes: Buffer;
      try {
        bytes = Buffer.from(file.data, "base64");
      } catch (error) {
        throw new Error(
          `Failed to decode base64 payload for "${file.fileName}": ${
            error instanceof Error ? error.message : String(error)
          }`,
        );
      }
      if (bytes.length > MAX_UPLOAD_BYTES_PER_FILE) {
        throw new UploadSizeError(bytes.length, MAX_UPLOAD_BYTES_PER_FILE);
      }
      totalBytes += bytes.length;
      if (totalBytes > MAX_UPLOAD_BYTES_PER_MESSAGE) {
        throw new UploadSizeError(totalBytes, MAX_UPLOAD_BYTES_PER_MESSAGE);
      }
      decoded.push({ file, bytes });
    }

    await fs.mkdir(manifestDirFor(cwd), { recursive: true });
    await this.ensureGitignoreEntry(cwd);

    const manifest = await this.readManifest(cwd);
    const created: SessionUpload[] = [];
    const createdAt = new Date().toISOString();

    for (const { file, bytes } of decoded) {
      const id = crypto.randomUUID();
      const sanitizedName = sanitizeFileName(file.fileName);
      const relPath = relativeUploadPath(id, sanitizedName);
      const absPath = path.join(cwd, relPath);
      await fs.writeFile(absPath, bytes);

      const entry: SessionUpload = {
        id,
        fileName: sanitizedName,
        mimeType: file.mimeType,
        size: bytes.length,
        createdAt,
        messageId,
        relativePath: relPath,
      };
      manifest.uploads.push({ ...entry, agentId } as SessionUpload & { agentId: string });
      created.push(entry);
    }

    await this.writeManifest(cwd, manifest);
    this.logger?.info(
      { agentId, count: created.length, totalBytes },
      "Persisted chat file uploads",
    );
    return created;
  }

  /**
   * Read bytes back from disk by workspace-relative path. Symmetric with
   * `SessionImageStore.readImageBytes` so the fetch-bytes RPC handler can
   * route uniformly. Traversal-guarded: refuses anything that escapes
   * `.appostle/tmp/uploaded_files/` or contains `..`.
   */
  async readUploadBytes(params: {
    cwd: string;
    relativePath: string;
  }): Promise<{ data: Buffer; mimeType: string; byteSize: number; fileName: string }> {
    const safe = path.posix.normalize(params.relativePath);
    if (!safe.startsWith(`${UPLOADS_DIR_REL}/`) || safe.includes("..")) {
      throw new Error(`Refused to read upload outside ${UPLOADS_DIR_REL}: ${params.relativePath}`);
    }
    const abs = path.join(params.cwd, safe);
    const data = await fs.readFile(abs);
    // The on-disk file name is `<id>__<sanitizedName>`; recover the user
    // portion for the Content-Disposition / response payload.
    const baseName = path.posix.basename(safe);
    const sep = baseName.indexOf("__");
    const fileName = sep >= 0 ? baseName.slice(sep + 2) : baseName;
    // Mime type lookup: read the manifest if present to recover the original
    // mime type (which we don't store in the filename). Falls back to
    // application/octet-stream — the caller can sniff if needed.
    let mimeType = "application/octet-stream";
    try {
      const manifest = await this.readManifest(params.cwd);
      const match = manifest.uploads.find((entry) => entry.relativePath === safe);
      if (match) {
        mimeType = match.mimeType;
      }
    } catch {
      // ignore — best-effort mime recovery
    }
    return { data, mimeType, byteSize: data.length, fileName };
  }

  /**
   * List every file stored for this workspace. Workspace-scoped, NOT
   * agent-scoped — uploads live under `<workspace>/.appostle/tmp/uploaded_files/`
   * and persist across agent lifecycles, so filtering by `agentId` would hide
   * files attached by older or sibling agents in the same workspace. (Mirrors
   * the image store's directory-scan behaviour.) The `agentId` parameter is
   * preserved on the API for backward-compat but ignored.
   *
   * Newest first.
   */
  async listUploads(params: ListUploadsParams): Promise<SessionUpload[]> {
    const manifest = await this.readManifest(params.cwd);
    return manifest.uploads
      .map(({ agentId: _agentId, ...rest }: SessionUpload & { agentId?: string }) => rest)
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  }

  /**
   * Delete a file by upload id. Workspace-scoped to match `listUploads` — any
   * agent in the workspace can delete any file. The `agentId` parameter is
   * preserved on the API for backward-compat but ignored.
   */
  async deleteUpload(params: DeleteUploadParams): Promise<{ deleted: boolean }> {
    const manifest = await this.readManifest(params.cwd);
    const idx = manifest.uploads.findIndex((entry) => entry.id === params.uploadId);
    if (idx === -1) {
      return { deleted: false };
    }
    const [removed] = manifest.uploads.splice(idx, 1);
    if (removed) {
      const absPath = path.join(params.cwd, removed.relativePath);
      try {
        await fs.unlink(absPath);
      } catch (error) {
        // The manifest is the source of truth — a missing file just means
        // someone deleted it out of band. Log and proceed so the manifest
        // doesn't permanently disagree with the filesystem.
        this.logger?.warn(
          { uploadId: params.uploadId, error: (error as Error).message },
          "deleteUpload: file already missing, removing manifest entry",
        );
      }
    }
    await this.writeManifest(params.cwd, manifest);
    return { deleted: true };
  }

  private async readManifest(cwd: string): Promise<ManifestEnvelopeV1> {
    const file = manifestPathFor(cwd);
    let raw: string;
    try {
      raw = await fs.readFile(file, "utf8");
    } catch (error) {
      if ((error as NodeJS.ErrnoException).code === "ENOENT") {
        return emptyManifest();
      }
      throw error;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      this.logger?.warn({ file }, "Manifest is not valid JSON; treating as empty");
      return emptyManifest();
    }
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "version" in parsed &&
      (parsed as { version?: number }).version === 1 &&
      Array.isArray((parsed as { uploads?: unknown }).uploads)
    ) {
      return parsed as ManifestEnvelopeV1;
    }
    return emptyManifest();
  }

  private async writeManifest(cwd: string, manifest: ManifestEnvelopeV1): Promise<void> {
    const file = manifestPathFor(cwd);
    const tmp = `${file}.tmp`;
    await fs.writeFile(tmp, JSON.stringify(manifest, null, 2), "utf8");
    await fs.rename(tmp, file);
  }

  /** Append a `.appostle/` ignore line to the workspace `.gitignore`, idempotently. */
  private async ensureGitignoreEntry(cwd: string): Promise<void> {
    const file = path.join(cwd, ".gitignore");
    let existing = "";
    try {
      existing = await fs.readFile(file, "utf8");
    } catch (error) {
      if ((error as NodeJS.ErrnoException).code !== "ENOENT") {
        throw error;
      }
    }
    if (existing.includes(GITIGNORE_MARKER_LINE)) {
      return;
    }
    const separator = existing.length === 0 || existing.endsWith("\n") ? "" : "\n";
    await fs.writeFile(file, `${existing}${separator}${GITIGNORE_BLOCK}`, "utf8");
  }
}
